import { api } from './api';

// ==================== DTOs ====================

export interface UserSignalDto {
  id: string;
  userId: string;
  signalType: string;
  sourceFeature: string;
  payloadJson?: string;
  createdAt: string;
}

export interface UserPatternDto {
  id: string;
  userId: string;
  patternType: string;
  patternDataJson: string;
  confidence: number;
  sensitivity: string;
  firstDetectedAt: string;
  lastConfirmedAt: string;
  dataPointCount: number;
  createdAt: string;
}

export interface UserInsightDto {
  id: string;
  userId: string;
  patternId?: string;
  insightType: string;
  insightText: string;
  actionSuggestion?: string;
  sensitivity: string;
  deliveryChannel: string;
  delivered: boolean;
  deliveredAt?: string;
  expiresAt: string;
  createdAt: string;
}

export interface UserPulseSettingsDto {
  id: string;
  userId: string;
  proactivityLevel: number;
  morningPagesInsightsEnabled: boolean;
  weeklyDigestEnabled: boolean;
}

export interface UpdatePulseSettingsRequest {
  proactivityLevel?: number;
  morningPagesInsightsEnabled?: boolean;
  weeklyDigestEnabled?: boolean;
}

// ==================== Service ====================

export const pulseService = {
  // Signals
  async getSignals(params?: { signalType?: string; sourceFeature?: string; startDate?: string; endDate?: string }): Promise<UserSignalDto[]> {
    const query = new URLSearchParams();
    if (params?.signalType) query.set('signalType', params.signalType);
    if (params?.sourceFeature) query.set('sourceFeature', params.sourceFeature);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    const qs = query.toString();
    return api.get<UserSignalDto[]>(`/pulse/signals${qs ? `?${qs}` : ''}`);
  },

  async emitSignal(signalType: string, sourceFeature: string, payloadJson?: string): Promise<UserSignalDto> {
    return api.post<UserSignalDto>('/pulse/signals', { signalType, sourceFeature, payloadJson });
  },

  async clearSignalHistory(): Promise<void> {
    await api.delete('/pulse/signals');
  },

  // Patterns
  async getPatterns(): Promise<UserPatternDto[]> {
    return api.get<UserPatternDto[]>('/pulse/patterns');
  },

  async analyzePatterns(): Promise<UserPatternDto[]> {
    return api.post<UserPatternDto[]>('/pulse/patterns/analyze', {});
  },

  // Insights
  async getInsights(params?: { channel?: string; delivered?: boolean }): Promise<UserInsightDto[]> {
    const query = new URLSearchParams();
    if (params?.channel) query.set('channel', params.channel);
    if (params?.delivered !== undefined) query.set('delivered', String(params.delivered));
    const qs = query.toString();
    return api.get<UserInsightDto[]>(`/pulse/insights${qs ? `?${qs}` : ''}`);
  },

  async generateInsights(): Promise<UserInsightDto[]> {
    return api.post<UserInsightDto[]>('/pulse/insights/generate', {});
  },

  async markInsightDelivered(id: string): Promise<UserInsightDto> {
    return api.post<UserInsightDto>(`/pulse/insights/${id}/delivered`, {});
  },

  // Settings
  async getSettings(): Promise<UserPulseSettingsDto> {
    return api.get<UserPulseSettingsDto>('/pulse/settings');
  },

  async updateSettings(request: UpdatePulseSettingsRequest): Promise<UserPulseSettingsDto> {
    return api.put<UserPulseSettingsDto>('/pulse/settings', request);
  },
};
